import { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation';
import {
  AssetImage,
  AssetV2,
  CategoryV2,
  Maybe,
  ModuleAssetPromo,
  ModuleCategory,
  ModuleCollage,
  ModuleCollageCollection,
  ModuleCollageThumbnail,
  ModuleCollection,
  ModuleDynamicCollection,
  ModulePreviewCollection,
  Need,
} from '@srgssr/rio-shared';

import { RANKING_SPACING, RANKING_TEASER_HEIGHT, RANKING_TEASER_WIDTH } from '../components/teasers/RankingTeaser';
import {
  RICH_ITEM_HEIGHT,
  RICH_ITEM_WIDTH,
  TEASER_AUDIO_HEIGHT,
  TEASER_AUDIO_WIDTH,
  TEASER_FOCUS_INDICATOR_SIZE,
  TEASER_HEIGHT,
  TEASER_WIDTH,
} from '../components/teasers/Teaser';
import { SIDE_MENU_MARGIN_LEFT } from './sideMenu';
import { BASE_WIDTH, scaleY, WINDOW_HEIGHT } from './window';

export const LIST_MODULE_ITEM_FOCUS_INDICATOR_SIZE = TEASER_FOCUS_INDICATOR_SIZE;
export const LIST_MODULE_ITEM_WIDTH = TEASER_WIDTH + LIST_MODULE_ITEM_FOCUS_INDICATOR_SIZE * 2;
export const LIST_MODULE_ITEM_HEIGHT = TEASER_HEIGHT + LIST_MODULE_ITEM_FOCUS_INDICATOR_SIZE * 2;
export const LIST_MODULE_ITEM_SPACE = 26;
export const LIST_MODULE_ITEM_WIDTH_WITH_SPACE = LIST_MODULE_ITEM_WIDTH + LIST_MODULE_ITEM_SPACE;

export const LIST_MODULE_AUDIO_ITEM_FOCUS_INDICATOR_SIZE = TEASER_FOCUS_INDICATOR_SIZE;
export const LIST_MODULE_AUDIO_ITEM_WIDTH = TEASER_AUDIO_WIDTH + LIST_MODULE_AUDIO_ITEM_FOCUS_INDICATOR_SIZE * 2;
export const LIST_MODULE_AUDIO_ITEM_HEIGHT = TEASER_AUDIO_HEIGHT + LIST_MODULE_AUDIO_ITEM_FOCUS_INDICATOR_SIZE * 2;
export const LIST_MODULE_AUDIO_ITEM_SPACE = 26;
export const LIST_MODULE_AUDIO_ITEM_WIDTH_WITH_SPACE = LIST_MODULE_AUDIO_ITEM_WIDTH + LIST_MODULE_AUDIO_ITEM_SPACE;

export const LIST_MODULE_RICH_ITEM_FOCUS_INDICATOR_SIZE = TEASER_FOCUS_INDICATOR_SIZE;
export const LIST_MODULE_RICH_ITEM_WIDTH = RICH_ITEM_WIDTH + LIST_MODULE_RICH_ITEM_FOCUS_INDICATOR_SIZE * 2;
export const LIST_MODULE_RICH_ITEM_HEIGHT = RICH_ITEM_HEIGHT + LIST_MODULE_RICH_ITEM_FOCUS_INDICATOR_SIZE * 2;
export const LIST_MODULE_RICH_ITEM_SPACE = 40;
export const LIST_MODULE_RICH_ITEM_HEIGHT_WITH_SPACE = RICH_ITEM_HEIGHT + LIST_MODULE_RICH_ITEM_SPACE;

export const LIST_MODULE_RANKING_TEASER_FOCUS_INDICATOR_SIZE = TEASER_FOCUS_INDICATOR_SIZE;
export const LIST_MODULE_RANKING_TEASER_WIDTH = RANKING_TEASER_WIDTH + RANKING_SPACING + LIST_MODULE_RANKING_TEASER_FOCUS_INDICATOR_SIZE;
export const LIST_MODULE_RANKING_TEASER_HEIGHT = RANKING_TEASER_HEIGHT + LIST_MODULE_RANKING_TEASER_FOCUS_INDICATOR_SIZE;
export const LIST_MODULE_RANKING_TEASER_SPACE = 26;
export const LIST_MODULE_RANKING_TEASER_WIDTH_WITH_SPACE = LIST_MODULE_RANKING_TEASER_WIDTH + LIST_MODULE_RANKING_TEASER_SPACE;

export const ASSET_PROMO_MODULE_ITEM_HEIGHT = 312;
export const CATEGORIES_MODULE_ITEM_HEIGHT = 246;

export const MODULE_TITLE_HEIGHT = 49;
export const MODULE_MARGIN_BOTTOM = 60;
export const LIST_MODULE_HEIGHT =
  scaleY(LIST_MODULE_ITEM_HEIGHT, null) + scaleY(MODULE_TITLE_HEIGHT, null) + scaleY(MODULE_MARGIN_BOTTOM, null);
export const LIST_MODULE_AUDIO_HEIGHT =
  scaleY(LIST_MODULE_AUDIO_ITEM_HEIGHT, null) + scaleY(MODULE_TITLE_HEIGHT, null) + scaleY(MODULE_MARGIN_BOTTOM, null);
export const ASSET_PROMO_MODULE_HEIGHT =
  scaleY(ASSET_PROMO_MODULE_ITEM_HEIGHT, null) + scaleY(MODULE_TITLE_HEIGHT, null) + scaleY(MODULE_MARGIN_BOTTOM, null);
export const CATEGORIES_MODULE_HEIGHT = scaleY(CATEGORIES_MODULE_ITEM_HEIGHT, null) + scaleY(MODULE_TITLE_HEIGHT, null);

// The beta banner is a wide promotional card shown without a swimlane title, so
// it is shorter than a standard row. It starts at the content left edge and
// reaches near the right edge like the swimlane teasers do, using a hand-tuned
// right margin, while keeping the design's 1320:180 aspect ratio so the art
// fills it without distortion. Both values are in design units (1920x1080).
const BETA_BANNER_ASPECT_RATIO = 1320 / 180;
const BETA_BANNER_RIGHT_MARGIN = 58;
export const BETA_BANNER_CARD_WIDTH = BASE_WIDTH - SIDE_MENU_MARGIN_LEFT - BETA_BANNER_RIGHT_MARGIN;
export const BETA_BANNER_CARD_HEIGHT = Math.round(BETA_BANNER_CARD_WIDTH / BETA_BANNER_ASPECT_RATIO);
// Thinner than a teaser's indicator: the same stroke reads much heavier drawn
// around a card this wide, so the banner uses the slim width the buttons do.
// It doubles as the gap reserved around the card, which keeps the card's left
// edge aligned with the teaser images of the other swimlanes.
export const BETA_BANNER_FOCUS_INDICATOR_SIZE = 7;
export const BETA_BANNER_MODULE_HEIGHT =
  scaleY(BETA_BANNER_CARD_HEIGHT, null) + scaleY(BETA_BANNER_FOCUS_INDICATOR_SIZE, null) * 2 + scaleY(MODULE_MARGIN_BOTTOM, null);

// The migration banner is the very same card as the beta banner, so it shares
// its geometry; aliased rather than reused under the beta name so the migration
// module reads on its own.
export const MIGRATION_BANNER_CARD_WIDTH = BETA_BANNER_CARD_WIDTH;
export const MIGRATION_BANNER_CARD_HEIGHT = BETA_BANNER_CARD_HEIGHT;
export const MIGRATION_BANNER_FOCUS_INDICATOR_SIZE = BETA_BANNER_FOCUS_INDICATOR_SIZE;
export const MIGRATION_BANNER_MODULE_HEIGHT = BETA_BANNER_MODULE_HEIGHT;

// The survey banner is the very same card again, aliased for the same reason.
export const SURVEY_BANNER_CARD_WIDTH = BETA_BANNER_CARD_WIDTH;
export const SURVEY_BANNER_CARD_HEIGHT = BETA_BANNER_CARD_HEIGHT;
export const SURVEY_BANNER_FOCUS_INDICATOR_SIZE = BETA_BANNER_FOCUS_INDICATOR_SIZE;
export const SURVEY_BANNER_MODULE_HEIGHT = BETA_BANNER_MODULE_HEIGHT;

/**
 * Copy sizes shared by the beta, migration and survey banner cards, so the three
 * never drift apart again. Design units against a 180-tall banner, so they scale
 * with the card at any screen size.
 *
 * The title is deliberately modest for a 900-weight display face: the longer
 * Crowdin strings have to clear the logo sitting on the right of every card.
 */
export const BANNER_TITLE_FONT_SIZE = Math.round(BETA_BANNER_CARD_HEIGHT * (28 / 180));
export const BANNER_SUBTITLE_FONT_SIZE = Math.round(BETA_BANNER_CARD_HEIGHT * (24 / 180));

const NEXT_ITEM_VISIBLE_OFFSET_Y = scaleY(80, null);

export const LIST_MODULE_OFFSET_Y = WINDOW_HEIGHT - LIST_MODULE_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;
export const LIST_MODULE_AUDIO_OFFSET_Y = WINDOW_HEIGHT - LIST_MODULE_AUDIO_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;
export const ASSET_PROMO_OFFSET_Y = WINDOW_HEIGHT - ASSET_PROMO_MODULE_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;
export const CATEGORIES_OFFSET_Y = WINDOW_HEIGHT - CATEGORIES_MODULE_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;
export const BETA_BANNER_OFFSET_Y = WINDOW_HEIGHT - BETA_BANNER_MODULE_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;
export const MIGRATION_BANNER_OFFSET_Y = WINDOW_HEIGHT - MIGRATION_BANNER_MODULE_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;
export const SURVEY_BANNER_OFFSET_Y = WINDOW_HEIGHT - SURVEY_BANNER_MODULE_HEIGHT - NEXT_ITEM_VISIBLE_OFFSET_Y;

type BaseModuleWithAssetIdsAssets = {
  assets: Array<Need<AssetV2, 'id'> & { image16x9WithTitle: Maybe<Need<AssetImage, 'url'>> }>;
};
type BaseModuleCollage = Need<ModuleCollage, 'id' | 'type' | 'title' | 'assetId' | 'version'> & {
  thumbnails: Array<Need<ModuleCollageThumbnail, 'url'>>;
};
type BaseModuleCollageCollection = Need<ModuleCollageCollection, 'id' | 'type' | 'title' | 'collectionId' | 'version'> & {
  logo: Need<ModuleCollageThumbnail, 'url' | 'alt'>;
  background: Need<ModuleCollageThumbnail, 'url' | 'alt'>;
  thumbnails: Array<Need<ModuleCollageThumbnail, 'url'>>;
};
type BaseModuleCollection = Need<ModuleCollection, 'id' | 'title' | 'type' | 'collectionType' | 'version'> & BaseModuleWithAssetIdsAssets;
type BaseModuleDynamicCollection = Need<ModuleDynamicCollection, 'id' | 'title' | 'type' | 'collectionType' | 'version'> &
  BaseModuleWithAssetIdsAssets;
type BaseModulePreviewCollection = Need<ModulePreviewCollection, 'id' | 'title' | 'type' | 'version'> & BaseModuleWithAssetIdsAssets;
export type BaseModuleAssetPromo = Need<ModuleAssetPromo, 'id' | 'title' | 'type' | 'version'> & { asset: Need<AssetV2, 'id'> };

export type BaseModuleCategories = Need<ModuleCategory, 'id' | 'title' | 'type' | 'version'> & {
  categories: Array<
    Need<CategoryV2, 'id' | 'title' | 'titleArtUrl' | 'thumbnailUrl'> & {
      page: {
        backgroundUrl: Maybe<string>;
      };
    }
  >;
};

export type BaseModule =
  | BaseModuleCollection
  | BaseModuleDynamicCollection
  | BaseModulePreviewCollection
  | BaseModuleCollage
  | BaseModuleCollageCollection
  | BaseModuleAssetPromo
  | BaseModuleCategories;

export type BaseListModule = Exclude<Exclude<BaseModule, BaseModuleAssetPromo>, BaseModuleCategories>;

export type ModuleActiveItem<T> = T & {
  index: number;
  layout: FocusableComponentLayout;
};

export type ModuleEventHandler<M, I> = {
  moduleOffsetTop: number;
  moduleIndex: number;
  module: M;
  item: I;
};
